/**
 * Centralized Application State
 * All shared state used across modules
 */

// Core audio state
export const audioState = {
  audioContext: null,
  audioBuffer: null,
  sourceNode: null,
  analyserNode: null,
  frequencyDataBuffer: null,
  timeDomainBuffer: null,    // Float32Array for raw samples (CQT support)
  featureExtractor: null  // AudioFeatureExtractor instance for enhanced features
};

// Playback state
export const playbackState = {
  isPlaying: false,
  isLoadingTrack: false,
  loadSequence: 0,
  startTime: 0,
  pauseTime: 0,
  animationId: null
};

// Playlist state
export const playlistState = {
  playlist: [],           // Array of {file, name, included, duration, audioBuffer, lyrics, lyricsFile, error, errorMessage, addedAt}
  selectedIndices: [],    // Currently selected items for move operations
  currentTrackIndex: 0,   // Currently playing track
  lastSelectedIndex: -1,  // For shift-click range selection
  durationsLoaded: false  // True when all durations have been processed (success or error)
};

// Playlist overlay state
export const playlistOverlayState = {
  isOpen: false,
  excludedVisible: false,
  lyricsVisible: false,
  lyricsTrackIndex: -1,
  dragState: {
    isDragging: false,
    draggedIndices: [],
    dragStartY: 0,
    currentY: 0,
    dropTargetIndex: -1,
    fromExcluded: false
  },
  filterText: '',  // Current filter text (name search)
  // Suno metadata filters
  sunoFilters: {
    genre: '',          // Filter by genre/tags text
    theme: '',          // Filter by lyrics theme/prompt text
    audioType: 'all',   // 'all', 'instrumental', 'vocal'
    likedStatus: 'all', // 'all', 'liked', 'not-liked'
    dateFrom: null,     // Date range start (ISO string or null)
    dateTo: null        // Date range end (ISO string or null)
  },
  sunoFiltersActive: false  // True if any Suno filter is active
};

// Playlist sorting state
export const sortingState = {
  mode: 'custom',        // 'custom', 'alphanumeric', 'date', 'duration'
  direction: 'asc',      // 'asc', 'desc'
  customOrder: [],       // Array of track references preserving custom order
  dontAskAgain: false    // Don't show confirmation modal
};

// UI mode state
export const uiState = {
  isTheaterMode: false,
  isLiveMode: false,
  liveStream: null,
  liveSourceNode: null,
  isLiveMonitoring: false,
  // Immersive mode controls (theater/fullscreen)
  immersiveControlsVisible: false,
  immersiveControlsTimeout: null
};

// Lyrics state
export const lyricsState = {
  lyricsPanelTrackIndex: -1,  // Track index for lyrics panel
  currentLyricsScrollParams: null,
  lyricsEnabled: true
};

// Export state
export const exportState = {
  currentExporter: null,  // Reference to active exporter for abort control
  exportWasAborted: false,
  isExporting: false,     // True when export is in progress (blocks playback/UI interactions)
  exportStartTime: null   // Timestamp when export started (for analytics)
};

// Background state
export const backgroundState = {
  type: 'solid',           // 'solid', 'gradient', 'pattern', 'image'
  color: '#0a0a0a',        // Primary color for solid/gradient
  color2: '#1a1a2e',       // Secondary color for gradient
  gradientDirection: 'vertical', // 'vertical', 'horizontal', 'diagonal', 'radial'
  pattern: 'none',         // 'none', 'dots', 'grid', 'lines', 'waves'
  patternColor: '#ffffff', // Pattern overlay color
  patternOpacity: 0.1,     // Pattern opacity (0-1)
  patternScale: 1,         // Pattern scale multiplier (0.1-10)
  patternSize: 2,          // Pattern element size in pixels (line width, dot diameter)
  imageUrl: null,          // Data URL or blob URL for custom image
  imageFit: 'cover'        // 'cover', 'contain', 'stretch', 'tile'
};

// Manager references (initialized during bootstrap)
export const managers = {
  visualizerManager: null,
  lyricsRenderer: null,
  perfMonitor: null
};

// Generator state (for Suno playlist generation)
export const generatorState = {
  // Form state - basic options
  genre: '',
  theme: '',
  lengthMode: 'songs',      // 'songs' | 'duration'
  songCount: 10,
  durationMinutes: 30,
  audioType: 'instrumental', // 'instrumental' | 'vocal'

  // Form state - advanced options (Issue #7)
  advancedMode: false,      // Show advanced generation options
  genres: [],               // Array of selected genres for mixing
  subGenres: [],            // Array of selected sub-genres
  mood: '',                 // Mood/atmosphere
  tempo: '',                // Tempo description
  instruments: [],          // Array of selected instruments
  vocals: {                 // Vocal specifications
    type: '',               // e.g., "female", "male", "choir"
    style: ''               // e.g., "powerful", "soft"
  },
  styleInfluences: [],      // Artists/songs for style reference (won't appear in output)
  era: '',                  // Era/period influence

  // Lyric generation options
  generateLyrics: false,    // Whether to generate lyrics
  lyricsTheme: '',          // Theme for lyrics
  lyricsMood: '',           // Mood for lyrics
  lyricsStyle: '',          // Lyrical style
  lyricsStructure: 'verse-chorus', // 'verse-chorus' | 'freeform' | 'spoken-word'

  // Generated content
  generatedPrompt: '',      // AI-generated Suno prompt
  generatedLyrics: '',      // AI-generated lyrics

  // Fix prompt/lyrics toggles (Issue #10)
  // When false (default): new prompt/lyrics variations are generated for each song for variety
  // When true: same prompt/lyrics are used for all songs in the generation
  fixedPrompt: false,       // Use same prompt for all songs
  fixedLyrics: false,       // Use same lyrics for all songs

  // Extension state (tokens managed by extension, not stored here)
  extensionAvailable: false,

  // Generation state
  isGenerating: false,
  isGeneratingContent: false, // AI content generation in progress
  currentTask: null,        // { id, status, progress, songs[] }
  error: null,

  // CAPTCHA state
  captchaRequired: false,
  captchaData: null
};

// Suno Manager state (full-size interface combining generator + library)
export const sunoManagerState = {
  isOpen: false,
  sourcePanel: null  // 'generator' | 'library' - which panel triggered the open
};

// Library browser state (for Suno library import)
export const libraryBrowserState = {
  // Panel state
  isOpen: false,

  // View state
  viewMode: 'grid',         // 'grid' | 'list'
  filter: 'all',            // 'all' | 'liked' (basic filter, kept for backward compat)

  // Advanced filter state (Issue #7)
  advancedFilters: {
    genre: '',              // Genre/tags filter text
    dateFrom: null,         // Date range start (ISO string or null)
    dateTo: null,           // Date range end (ISO string or null)
    audioType: 'all',       // 'all' | 'instrumental' | 'vocal'
    theme: '',              // Lyrics theme/prompt filter text
    likedStatus: 'all',     // 'all' | 'liked' | 'not-liked'
    generationOrder: 'all', // 'all' | 'first' | 'second' (1st vs 2nd in generation set)
    minLength: null         // Minimum length filter in seconds (Issue #13)
  },
  filterPanelOpen: false,   // Whether the advanced filter panel is open

  // Data state
  songs: [],                // Current page of songs
  hasMore: false,           // More pages available
  total: 0,                 // Total song count
  nextCursor: null,         // Cursor for next page (v3 pagination)
  pageSize: 20,             // Items per page

  // Search state
  searchQuery: '',
  isSearching: false,

  // Selection state
  selectionMode: false,     // Batch selection enabled
  selectedIds: new Set(),   // Selected song IDs

  // Loading state
  isLoading: false,
  error: null,

  // Export state
  isExporting: false,
  exportProgress: {
    current: 0,
    total: 0
  }
};
